/* Copyright (c) 2020-present CloudIO Inc */

import { Path, setIn } from '@cloudio-saas/mutx';
import {
  isSamePath,
  useLatest,
  usePathMemo,
  useStateXRemover,
  useStateXSetter,
} from '@cloudio-saas/statex';
import useAnimationFrame from 'hooks/useAnimationFrame';
import useStateChanges, { Removal, Update } from 'hooks/useStateChanges';
import { useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import {
  EnterRequest,
  hasWSError,
  IOActive,
  TouchMessage,
} from '@cloudio-saas/datasource-types';
import { ActivePubSub, State } from 'utils/Active';
import { useWS } from '../streams/useWebSocket';
import assert from 'utils/assert';

export interface Active<T> extends IOActive {
  data: T;
}

export interface TouchEvent<T> {
  active: Active<T>;
  type: 'Touch';
  touchData: T;
}

export interface EnterExitEvent<T> {
  active: Active<T>;
  type: 'Enter' | 'Exit';
}

export type Event<T> = TouchEvent<T> | EnterExitEvent<T>;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const EMPTY_ARRAY: readonly Active<any>[] = Object.freeze([]);
const _non_existing_path_: Path = ['_non_existing_path_'];

export interface ChangeData {
  updates?: Update[];
  removals?: Removal[];
  $status: 'active' | 'passive';
}

export default function useIOActive<T extends ChangeData>(
  appUid: string,
  topic: string,
  id: string,
  _path: Path = _non_existing_path_,
  onEvent?: (event: Event<T>) => void,
) {
  const path = usePathMemo(_path);
  const { sendRequest, addListener, addReconnectListener } = useWS();
  const [actives, setActives] = useState<readonly Active<T>[]>(EMPTY_ARRAY);
  const idRef = useRef('');
  const enteredRef = useRef(false);
  const ref = useRef({
    updatedPaths: new Set<Path>(),
    removedPaths: new Set<Path>(),
  });
  const set = useStateXSetter();
  const remove = useStateXRemover();
  const activesRef = useLatest(actives);
  const onEventRef = useLatest(onEvent);
  const currentStatus = useRef<State>('active');

  const sendUpdate = useAnimationFrame((data: T) => {
    if (idRef.current && actives.length) {
      sendRequest({ type: 'Touch', _id: idRef.current, data }, { appUid });
    }
  });

  const sendStateUpdate = useAnimationFrame((data: T) => {
    if (idRef.current && activesRef.current.length) {
      sendRequest({ type: 'Touch', _id: idRef.current, data }, { appUid });
    }
  });

  useStateChanges(path, (updates, removals) => {
    const { updatedPaths, removedPaths } = ref.current;
    updates = updates.filter((u) => {
      if (updatedPaths.has(u.path)) {
        updatedPaths.delete(u.path);
        return false;
      }
      // ignore addition of the root path
      return !isSamePath(u.path, path);
    });
    removals = removals.filter((r) => {
      if (removedPaths.has(r.path)) {
        removedPaths.delete(r.path);
        return false;
      }
      // ignore removal of the root path
      return !isSamePath(r.path, path);
    });

    if (updates.length || removals.length) {
      sendStateUpdate({ updates, removals });
    }
  });

  useEffect(() => {
    setActives(EMPTY_ARRAY);
    idRef.current = '';
    const req: EnterRequest<T> = {
      appUid,
      topic,
      id,
      type: 'Enter',
      data: { $status: 'active' } as T,
    };
    const unlisten = addListener((message) => {
      switch (message.type) {
        case 'Enter': {
          const {
            msg: { active, data },
            to,
          } = message;
          if (
            to === idRef.current &&
            active.topic === topic &&
            active.id === id &&
            active.appUid === appUid &&
            active._id !== idRef.current
          ) {
            let newActive = true;
            setActives((a) => {
              if (a.find((v) => v._id === active._id)) {
                newActive = false;
                return a;
              }
              return [...a, { ...active, data }];
            });
            if (newActive) {
              onEventRef.current?.({
                type: 'Enter',
                active: { ...active, data },
              });
            }
          }
          break;
        }
        case 'Touch': {
          const {
            msg: { _id, data },
            to,
          } = message as TouchMessage<T>;
          if (_id !== idRef.current && to === idRef.current && data) {
            ReactDOM.unstable_batchedUpdates(() => {
              let updatedActive: Active<T> | null = null;
              setActives((a) => {
                const m = a.find((v) => v._id === _id);
                if (m) {
                  updatedActive = { ...m, data: { ...m.data, ...data } };
                  const _result = setIn(a, [a.indexOf(m)], updatedActive);
                  setTimeout(() => {
                    if (idRef.current && updatedActive) {
                      onEventRef.current?.({
                        type: 'Touch',
                        active: { ...updatedActive },
                        touchData: data,
                      });
                    }
                  }, 1);
                  return _result;
                }
                return a;
              });

              if (path !== _non_existing_path_) {
                const { updatedPaths, removedPaths } = ref.current;
                data.updates?.forEach((update) => {
                  let containsPath = false;
                  updatedPaths.forEach((p) => {
                    if (isSamePath(p, update.path)) {
                      containsPath = true;
                    }
                  });
                  if (!containsPath) {
                    updatedPaths.add(update.path);
                  }
                  set(update.path, update.value);
                });
                data.removals?.forEach((removal) => {
                  let containsPath = false;
                  removedPaths.forEach((p) => {
                    if (isSamePath(p, removal.path)) {
                      containsPath = true;
                    }
                  });
                  if (!containsPath) {
                    removedPaths.add(removal.path);
                  }
                  remove(removal.path);
                });
              }
            });
          }
          break;
        }
        case 'Exit': {
          const {
            msg: { _id: aid },
            to,
          } = message;
          if (to !== idRef.current) {
            break;
          }
          if (aid === idRef.current) {
            // self exit! re-activate
            setActives(EMPTY_ARRAY);
            sendRequest(req, { appUid })[0].then((response) => {
              if (!hasWSError(response)) {
                idRef.current = response.data._id as string;
              }
            });
            return;
          }

          let exitedActive: Active<T> | null = null;
          setActives((a) =>
            a.filter((v) => {
              if (v._id === aid) {
                exitedActive = v;
                setTimeout(() => {
                  if (idRef.current && exitedActive) {
                    onEventRef.current?.({
                      type: 'Exit',
                      active: exitedActive,
                    });
                  }
                }, 1);
                return false;
              }
              return true;
            }),
          );
          break;
        }
        default:
      }
    });

    if (req.id && !enteredRef.current) {
      enteredRef.current = true;
      // avoid sending request with empty pageId
      sendRequest(req, { appUid })[0].then((response) => {
        if (!hasWSError(response)) {
          if (!enteredRef.current) {
            // already unmounted! hence send Exit back
            sendRequest(
              { type: 'Exit', _id: response.data._id as string },
              { appUid },
            );
          } else {
            idRef.current = response.data._id as string;
          }
        }
      });
    }

    const unlisten2 = addReconnectListener(() => {
      setActives(EMPTY_ARRAY);
      const _id = idRef.current;
      if (_id) {
        idRef.current = '';
        sendRequest({ type: 'Exit', _id }, { appUid });

        sendRequest(req, { appUid })[0].then(async (response) => {
          if (!hasWSError(response)) {
            const oldId = idRef.current;
            assert(!oldId, `idRef exists! ${topic}.${id}`);
            idRef.current = response.data._id as string;
            if (oldId) {
              sendRequest({ type: 'Exit', _id: oldId }, { appUid });
            }
          }
        });
      }
    });

    const unlisten3 = ActivePubSub.sub(
      'StateChange',
      async (_, { nextState }) => {
        if (currentStatus.current !== nextState) {
          currentStatus.current = nextState;
          sendUpdate({ $status: nextState }, appUid);
        }
      },
    );

    const unlisten4 = ActivePubSub.sub(
      'AppStateChange',
      async (_, { nextState }) => {
        if (currentStatus.current !== nextState) {
          currentStatus.current = nextState;
          sendUpdate({ $status: nextState }, appUid);
        }
      },
    );

    return () => {
      setActives(EMPTY_ARRAY);
      unlisten();
      unlisten2();
      unlisten3();
      unlisten4();

      if (idRef.current) {
        sendRequest({ type: 'Exit', _id: idRef.current }, { appUid });
        idRef.current = '';
      } else {
        // unmounting before getting the enter response
      }
      enteredRef.current = false;
    };
  }, [
    addListener,
    addReconnectListener,
    appUid,
    id,
    onEventRef,
    path,
    remove,
    sendRequest,
    sendUpdate,
    set,
    topic,
  ]);

  return { actives, sendUpdate };
}
